/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React from 'react'
import {Favorite, FavoriteApp} from '../../../../model/favorites'
import {useDrag} from 'react-dnd'
import {DragIndicator} from '@mui/icons-material'
import {Badge, Box, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Typography} from '@mui/material'
import {DefaultAppIcon} from '../../../../icon/DefaultAppIcon'
import {asPermission, TranslatedText, usePermissions, useThemeName} from '@enisco/mcc-react-lib'
import {FavoriteContextMenuButton} from './FavoriteContextMenuButton'
import useFavoriteApp from '../../../../hooks/useFavoriteApp'
import {useTheme} from '@mui/material/styles'

interface Props {
  favoriteApp: FavoriteApp
  index: number
  handleClose: () => void
}

const FavoriteViewerFavorite: React.FC<Props> = ({
  favoriteApp,
  index,
  handleClose
}) => {
  const [themeName] = useThemeName()
  const [, setFavoriteApp] = useFavoriteApp()
  const hasAccess = usePermissions()
  const theme = useTheme()

  const openFavorite = (favorite: Favorite) => {
    handleClose()
    setFavoriteApp(favorite)
  }

  const [, drag] = useDrag({
    type: 'Favorite',
    item: () => {
      return {favoriteApp, index}
    }
  })

  const getBadge = (favoriteBadge: FavoriteApp) => {
    if (favoriteBadge.favorite.user) {
      return themeName === 'dark'
        ? '/mcc-portal-ui/badges/ic_favbadge_dark.svg'
        : '/mcc-portal-ui/badges/ic_favbadge_light.svg'
    } else {
      return themeName === 'dark'
        ? '/mcc-portal-ui/badges/ic_favbadge_global_dark.svg'
        : '/mcc-portal-ui/badges/ic_favbadge_global_light.svg'
    }
  }

  return (
    <div ref={(r) => {
      drag(r)
    }}>
      <ListItem
        sx={{
          paddingLeft: 4,
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            backgroundColor: themeName === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)"
          }}}
        onClick={() => openFavorite(favoriteApp.favorite)}
        key={favoriteApp.favorite.id}
        data-testid={'favorite-list-' + index}
      >
        <Box sx={{paddingRight: 2}}>
          <DragIndicator />
        </Box>

        <ListItemIcon>
          <Badge
            badgeContent={
              <img src={getBadge(favoriteApp)} width={'15px'} alt='' />
            }
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          >
            {favoriteApp.app.icon ? (
              <img
                src={favoriteApp.app.icon}
                width={'32px'}
                height={'32px'}
                alt=''
              />
            ) : (
              <DefaultAppIcon width={'32px'} height={'32px'} />
            )}
          </Badge>
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant='body1'>
              <TranslatedText text={favoriteApp.favorite.description} />
            </Typography>
          }
          secondary={
            <Typography variant='caption'>
              <TranslatedText text={favoriteApp.app.description} />
            </Typography>
          }
        />
        {favoriteApp.favorite.user ||
        hasAccess(asPermission('mcc-portal', '_global-favorites', '_edit')) ? (
          <ListItemSecondaryAction>
            <FavoriteContextMenuButton favorite={favoriteApp.favorite} />
          </ListItemSecondaryAction>
        ) : undefined}
      </ListItem>
    </div>
  )
}

export default FavoriteViewerFavorite
